import { Inject, inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HTTP_INTERCEPTORS, HttpErrorResponse } from '@angular/common/http';

import { AccessTokenService } from '@core/services/access-token.service';

import { Observable, throwError } from 'rxjs';
import { catchError, delay, filter, switchMap, take } from 'rxjs/operators';
import { UtilsService } from '@core/services/utils.service';

class ErrorInstance {

    constructor(private _errorResponse: HttpErrorResponse) { }

    get statusText() {
        return this._errorResponse.statusText;
    }

    get errorCode() {
        return this._errorResponse.status;
    }

    private get _parsedError(): any {
        const raw = this._errorResponse.error;
        if (!raw) return null;
        if (typeof raw === 'string') {
            try { return JSON.parse(raw); } catch { return { error_message: raw }; }
        }
        return raw;
    }

    get errorName() {
        const parsed = this._parsedError;
        if (!parsed) return this._errorResponse.name;
        if (parsed.error && typeof parsed.error === 'object') return parsed.error.name;
        return this._errorResponse.name;
    }

    get errorMessage() {
        const parsed = this._parsedError;
        if (!parsed) return undefined;
        if (parsed.error) {
            return typeof parsed.error === 'string' ? parsed.error : parsed.error.message;
        }
        return parsed.error_message || parsed.message;
    }
};

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {

    /**
     * constructor
     * 
     * @param {Router} _router
     * @param {AccessTokenService} _accessTokenService
     * @param {UtilsService} _utilsService
     */
    constructor(private _router: Router,
        private _accessTokenService: AccessTokenService,
        private _utilsService: UtilsService) { }

    /**
     * Handle all kind of errors
     * 
     * 401 => Refresh access token
     * Others => ThrowError
     * Unknown error => Redirect to login and throwError
     * 
     * @param {HttpRequest<any>} request
     * @param {HttpHandler} next
     * @returns {Observable<HttpEvent<any>>}
     */
    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(request).pipe(
            catchError(errorResponse => {
                // scheduler service exception
                if (errorResponse instanceof HttpErrorResponse) {
                    const errorInstance = new ErrorInstance(errorResponse);
                    if (errorInstance.statusText != "Unknown Error") {
                        // handle token expired
                        const urlSet = new Set(request.url.toLowerCase().split('/'));
                        if (errorInstance.errorCode === 401 &&
                            (errorInstance.errorMessage === "jwt expired" ||
                                errorInstance.errorMessage === "Response status code does not indicate success: 401 (Unauthorized)." ||
                                urlSet.has('schedules')))
                            return this.handle401Error(request, next);

                        switch (errorInstance.errorCode) {
                            case 401:
                            case 403:
                                if (!this._router.url.startsWith('/twofa')) {
                                    this._utilsService.clearCookie();
                                    this._router.navigate(['/login']);
                                }
                                break;
                            default:
                                break;
                        }

                        return throwError(errorInstance.errorMessage);
                    }
                    else return throwError("Unknown Error");
                }
                else return throwError("Unknow Error");
            })
        );
    }

    /**
     * Refresh the access token
     * 
     * @param {HttpRequest<any>} request 
     * @param {HttpHandler} next 
     * @returns {Observable<HttpEvent<any>>}
     */
    private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const lastUpdateTimeStamp = this._accessTokenService.accessTokenTimeStamp;
        const currentTimeStamp = new Date().getTime();
        if (!this._accessTokenService.refreshFlag && currentTimeStamp - lastUpdateTimeStamp > 600000) {
            this._accessTokenService.refreshFlag = true;
            this._accessTokenService.clearAllInfo();
            return this._accessTokenService.refreshAccessToken().pipe(
                switchMap((token) => {
                    this._accessTokenService.accessTokenTimeStamp = currentTimeStamp;
                    this._accessTokenService.refreshFlag = false;
                    this._accessTokenService.setToken(token.data);
                    return next.handle(request.clone());
                }),
                catchError((error) => {
                    this._accessTokenService.refreshFlag = false;
                    this._utilsService.clearCookie();
                    this._router.navigate(['/login']);
                    return throwError(error);
                })
            );
        }

        return this._accessTokenService.currentAccessToken.pipe(
            filter(token => token !== null),
            take(1),
            delay(100),
            switchMap(() => next.handle(request.clone()))
        );
    }
}

export const HTTPErrorHandlerProvider = {
    provide: HTTP_INTERCEPTORS,
    useClass: ErrorInterceptor,
    multi: true
};